<script setup lang="ts">
import type { Components } from '@/api/generated/api';
import DocumentPenIcon from '@/assets/icons/DocumentPenIcon.vue';
import SupportMeasureIcon from '@/assets/icons/SupportMeasureIcon.vue';
import { EQuickEntryKind } from '@/composables/quickEntry/quickEntry.enums';
import { useSupportMeasureFeature } from '@/composables/supportMeasureEntry/useSupportMeasureFeature';
import { usePermissions } from '@/composables/usePermissions';
import { COLOR_TINT } from '@/constants';
import BillingProBadge from '@/core/billing/BillingProBadge.vue';
import PositionablePopover from '@/core/dialogs/PositionablePopover.vue';
import PerformanceAssessmentIcon from '@/core/elements/PerformanceAssessmentIcon.vue';
import PerformanceAssessmentSelectEmptyState from '@/core/observation/PerformanceAssessmentSelectEmptyState.vue';
import SelectionPopoverDivider from '@/core/selects/SelectionPopoverDivider.vue';
import SelectionPopoverOptionRow from '@/core/selects/SelectionPopoverOptionRow.vue';
import { getPerformanceAssessmentName } from '@/utils/performance.utils';
import { computed, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';

const emit = defineEmits<{
  'select:note': [];
  'select:performance-assessment': [performanceAssessmentId: number];
  'select:support-measure': [];
  hide: [];
}>();

const props = withDefaults(
  defineProps<{
    anchorElement: HTMLElement | null;
    eligiblePerformanceAssessments: Components.Schemas.PerformanceAssessmentDto[];
    mobileDrawer?: boolean;
    /** omitted by the "Neuer Eintrag" split button: its menu starts a new entry, so no type is checked */
    selectedKind?: EQuickEntryKind | null;
    selectedPerformanceAssessmentId?: number | null;
  }>(),
  {
    mobileDrawer: false,
    selectedKind: null,
    selectedPerformanceAssessmentId: null,
  },
);

const { t } = useI18n();
const { canManagePerformanceAssessments } = usePermissions();
const { hasSupportMeasures } = useSupportMeasureFeature();

const popoverRef = useTemplateRef('popoverRef');

const isNoteSelected = computed(() => props.selectedKind === EQuickEntryKind.Note);
const isSupportMeasureSelected = computed(() => props.selectedKind === EQuickEntryKind.SupportMeasure);

const isAssessmentSelected = (performanceAssessment: Components.Schemas.PerformanceAssessmentDto): boolean =>
  props.selectedKind === EQuickEntryKind.PerformanceAssessment &&
  performanceAssessment.id === props.selectedPerformanceAssessmentId;

const show = (): void => {
  void popoverRef.value?.show();
};
const hide = (): void => popoverRef.value?.hide();

defineExpose({ show, hide, isOpen: computed(() => !!popoverRef.value?.isOpen) });
</script>

<template>
  <PositionablePopover
    ref="popoverRef"
    scroll-mode="content"
    :anchor-element="props.anchorElement"
    class="w-quick-entry-popover"
    flip-strategy="both"
    lazy-content
    :mobile-drawer-label="$t('general.selectEntryType')"
    :mobile-drawer-mode="props.mobileDrawer"
    :offset-y="10"
    @hide="emit('hide')"
  >
    <template #default="{ mobileDrawer: isMobileDrawer }">
      <div
        class="flex min-h-0 min-w-0 w-full flex-1 flex-col overflow-y-auto overscroll-contain px-smd py-smd"
        :class="!isMobileDrawer && 'max-h-selection-popover'"
      >
        <SelectionPopoverOptionRow class="shrink-0" :selected="isNoteSelected" @select="emit('select:note')">
          <template #leading="{ selected }">
            <DocumentPenIcon class="size-icon-md shrink-0" :class="selected ? 'text-tint' : 'text-primary'" />
          </template>
          {{ $t('quickEntry.typeNote') }}
        </SelectionPopoverOptionRow>

        <SelectionPopoverDivider class="shrink-0" />
        <div class="flex w-full shrink-0 items-center px-sm py-sm">
          <span class="text-body font-semibold text-secondary">
            {{ $t('quickEntry.sectionPerformanceAssessment') }}
          </span>
        </div>

        <ul v-if="props.eligiblePerformanceAssessments.length" class="flex flex-col gap-px">
          <li v-for="performanceAssessment in props.eligiblePerformanceAssessments" :key="performanceAssessment.id">
            <SelectionPopoverOptionRow
              :selected="isAssessmentSelected(performanceAssessment)"
              @select="emit('select:performance-assessment', performanceAssessment.id)"
            >
              <template #leading="{ selected }">
                <PerformanceAssessmentIcon
                  :symbol="performanceAssessment.symbol"
                  :color="selected ? COLOR_TINT : undefined"
                />
              </template>
              {{ getPerformanceAssessmentName(performanceAssessment, t) }}
            </SelectionPopoverOptionRow>
          </li>
        </ul>
        <PerformanceAssessmentSelectEmptyState
          v-else
          :can-manage-performance-assessments="canManagePerformanceAssessments"
        />

        <SelectionPopoverDivider class="shrink-0" />
        <SelectionPopoverOptionRow
          class="shrink-0"
          :selected="isSupportMeasureSelected"
          @select="emit('select:support-measure')"
        >
          <template #leading="{ selected }">
            <SupportMeasureIcon class="size-icon-md shrink-0" :class="selected ? 'text-tint' : 'text-primary'" />
          </template>
          <span class="inline-flex items-center gap-md">
            {{ $t('quickEntry.typeSupportMeasure') }}
            <BillingProBadge v-if="!hasSupportMeasures" theme="dark" />
          </span>
        </SelectionPopoverOptionRow>
      </div>
    </template>
  </PositionablePopover>
</template>
